var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Banner, Button, Divider, FlexBox, Modal, ModalBody, ModalFoot, ModalHead, Text, } from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { useCallback, useState } from 'react';
import { ajaxSend } from 'api/api';
import { QueueSelect } from 'components/queueSelect';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { FacebookLoginButton } from '../facebookLoginButton';
import { InstagramFeatures } from '../instagramFeatures';
import styles from './instagramAuthenticator.module.scss';
/** The authentication steps. */
const steps = [
    {
        text: 'Eligible Instagram Business account.',
        link: 'https://www.facebook.com/help/instagram/502981923235522',
    },
    {
        text: 'Instagram connected to a Facebook page.',
        link: 'https://www.facebook.com/business/help/898752960195806',
    },
    {
        text: 'Instagram messaging permissions enabled.',
        link: 'https://www.facebook.com/help/instagram/791161338412168',
    },
];
/** A component that displays an instagram login modal and it's associated modal. */
export const InstagramAuthenticator = ({ appId, prerequisitesButtonProps, children, onSuccess, }) => {
    const [status, setStatus] = useState('uninitialized');
    const [queueId, setQueueId] = useState();
    const [step, setStep] = useState();
    const [facebookData, setFacebookData] = useState();
    const [authenticationError, setAuthenticationError] = useState();
    const organizationId = useAppSelector((state) => state.settings.organization.organization.id);
    const [features, setFeatures] = useState({
        isStoryCommentsEnabled: true,
        isStoryMentionsEnabled: true,
    });
    /** Resets the modal. */
    const resetModal = useCallback(() => {
        setStatus('uninitialized');
        setQueueId(undefined);
        setStep(undefined);
        setFacebookData(undefined);
        setAuthenticationError(undefined);
    }, []);
    /** Handles the facebook authentication. */
    const handleFacebookAuthenticated = useCallback((facebookAuthenticationData) => {
        setFacebookData(facebookAuthenticationData);
        setStep('select-queue');
    }, []);
    const handleFacebookError = useCallback((facebookAuthenticationError) => {
        setAuthenticationError(facebookAuthenticationError);
    }, []);
    /** Handles confirming the queue. */
    const handleQueueConfirm = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setStatus('pending');
            yield ajaxSend({
                path: '/v1/facebook-messenger/connect',
                method: 'PUT',
                data: {
                    default_queue_id: queueId,
                    organization_id: organizationId,
                    application_id: appId,
                    user_id: facebookData === null || facebookData === void 0 ? void 0 : facebookData.userId,
                    user_access_token: facebookData === null || facebookData === void 0 ? void 0 : facebookData.accessToken,
                    connect_facebook: false,
                    connect_instagram: true,
                    instagram_messaging_settings: {
                        story_comments_enabled: features.isStoryCommentsEnabled,
                        story_mentions_enabled: features.isStoryMentionsEnabled,
                    },
                },
            });
            resetModal();
            onSuccess();
        }
        catch (error) {
            setStatus('rejected');
        }
    }), [
        appId,
        facebookData === null || facebookData === void 0 ? void 0 : facebookData.accessToken,
        facebookData === null || facebookData === void 0 ? void 0 : facebookData.userId,
        features,
        onSuccess,
        organizationId,
        queueId,
        resetModal,
    ]);
    return (_jsxs(_Fragment, { children: [_jsx(Button, Object.assign({ onClick: () => setStep('prerequisites') }, prerequisitesButtonProps, { children: children })), _jsxs(Modal, Object.assign({ isOpen: step === 'prerequisites', className: styles.modal, onClose: resetModal }, { children: [_jsx(ModalHead, { title: "Instagram for business", onClose: resetModal }), _jsx(ModalBody, { children: _jsxs(FlexBox, Object.assign({ gap: '300', flexDirection: "column" }, { children: [(authenticationError === null || authenticationError === void 0 ? void 0 : authenticationError.error) && (_jsx(Banner, { variant: "fail", description: _jsx(Text.Paragraph, { children: authenticationError.error === 'incorrect_configuration'
                                            ? `The selected Facebook pages are not associated with the selected Instagram account. Please restart the configuration process and select the proper page.`
                                            : `No Facebook page was selected. Please restart the configuration process and select the page connected with the desired Instagram account.` }) })), _jsx(Text.Paragraph, { children: "Instagram is one of the most popular social applications in the world. Instagram is a Facebook company and requires access to a linked Facebook account and the mobile Instagram app for channel creation." }), _jsx(Divider, { size: "xSmall" }), _jsx(Text.Paragraph, { children: "What you'll need" }), _jsx("ul", { children: steps.map(({ text, link }, index) => (_jsx("li", { children: _jsx(Text.Paragraph, { children: text }) }, index))) }), _jsxs(Text.Paragraph, { children: ["Complete instructions and walk-through can be found", ' ', _jsx("span", Object.assign({ role: "button", className: styles.articleLink, onClick: () => {
                                                elevioClient.openArticle('725');
                                            } }, { children: "here" }))] })] })) }), _jsxs(FlexBox, Object.assign({ justifyContent: "flex-end", gap: "16px", padding: '32px' }, { children: [_jsx(Button, Object.assign({ variant: "tertiary", onClick: resetModal }, { children: "Cancel" })), _jsx(FacebookLoginButton, Object.assign({ scope: "pages_show_list,instagram_basic,instagram_manage_messages,pages_manage_metadata,pages_read_engagement,pages_messaging", onSuccess: handleFacebookAuthenticated, onError: handleFacebookError, platform: "instagram" }, { children: "Continue" }))] }))] })), _jsxs(Modal, Object.assign({ isOpen: step === 'select-queue', className: styles.modal, onClose: resetModal }, { children: [_jsx(ModalHead, { title: "Instagram for business", onClose: resetModal }), _jsx(ModalBody, { children: _jsxs("div", Object.assign({ className: "messenger-form__field" }, { children: [_jsx(Text.Paragraph, { children: "All the Instagram conversations will be sent to the selected queue." }), _jsx("label", Object.assign({ htmlFor: "messenger-page-queue", className: "messenger-form__field__label" }, { children: "Queue" })), _jsx(QueueSelect, { onSelect: (queue) => setQueueId(queue.id) }), _jsx(InstagramFeatures, { onChange: setFeatures })] })) }), _jsx(ModalFoot, { confirmName: "Continue", confirmOnClick: handleQueueConfirm, confirmProps: {
                            disabled: queueId === undefined,
                            isLoading: status === 'pending',
                        }, cancelOnClick: resetModal })] }))] }));
};
